// ============================================================
// cf-file-upload.js � Unified File Upload Widget
// ============================================================
// 3 modes:
//   avatar � circular preview with camera button (user avatar)
//   single � drop zone + file card with progress bar
//   multi  � drop zone + file table with docType per row
//
// Public API:
//   CF.fileUpload.init(containerId, options)
//   CF.fileUpload.setFile(containerId, data)
//   CF.fileUpload.getResult(containerId)
//   CF.fileUpload.reset(containerId)
//
// Options:
//   mode:      'avatar' | 'single' | 'multi'   (required)
//   module:    string   (required, maps to FileUpload.{module} config)
//   readonly:  boolean  (default false)
//   accept:    string   (single/multi: '.pdf,.jpg', avatar: hardcoded)
//   maxSizeMb: number   (single/multi: max file size, avatar: 2)
//   docType:   string   (single: fixed docType for the file)
//   maxFiles:  number   (multi only, default 10)
//   docTypes:  string[] (multi: selectable per row, e.g. ['Invoice','Receipt'])
//   onUploaded: fn      (callback after each successful upload)
//   onRemoved:  fn      (callback after file removed)
// ============================================================

window.CF = window.CF || {};

CF.fileUpload = (function () {
    'use strict';

    var _states = {};

    function _esc(s) { return CF.formHelpers ? CF.formHelpers.escHtml(s) : s; }

    function _apiBase() {
        return (typeof API_BASE_URL !== 'undefined' && API_BASE_URL) ? API_BASE_URL : '';
    }

    function _formatSize(bytes) {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
    }

    function _fileIcon(ext) {
        ext = (ext || '').toLowerCase().replace('.', '');
        switch (ext) {
            case 'pdf':  return 'fa-solid fa-file-pdf';
            case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': case 'webp':
                return 'fa-solid fa-file-image';
            case 'xlsx': case 'xls': case 'csv':
                return 'fa-solid fa-file-excel';
            case 'docx': case 'doc':
                return 'fa-solid fa-file-word';
            case 'zip': case 'rar': case '7z':
                return 'fa-solid fa-file-zipper';
            default: return 'fa-solid fa-file';
        }
    }

    function _isImage(ext) {
        ext = (ext || '').toLowerCase().replace('.', '');
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].indexOf(ext) !== -1;
    }

    function _getExt(filename) {
        return '.' + (filename || '').split('.').pop().toLowerCase();
    }

    // ??????????????????????????????????????????
    // init
    // ??????????????????????????????????????????
    function init(containerId, options) {
        var opts = options || {};
        var mode = opts.mode || 'avatar';

        _states[containerId] = {
            mode:       mode,
            module:     opts.module     || 'User',
            readonly:   !!opts.readonly,
            accept:     opts.accept     || (mode === 'avatar' ? '.jpg,.jpeg,.png' : ''),
            maxSizeMb:  opts.maxSizeMb  || (mode === 'avatar' ? 2 : 10),
            docType:    opts.docType    || null,
            maxFiles:   opts.maxFiles   || 10,
            docTypes:   opts.docTypes   || [],
            onUploaded: opts.onUploaded || null,
            onRemoved:  opts.onRemoved  || null,
            // runtime state
            uploading:  false,
            tempFileId: null,           // avatar & single
            fileInfo:   null,           // single: { tempFileId, docType, name, size, ext }
            files:      [],             // multi: [ { id, tempFileId, docType, name, size, ext, existingId, uploading, progress } ]
            _xhrs:      {}             // track XHR per uid for cancel
        };

        if (mode === 'avatar')  _renderAvatar(containerId);
        if (mode === 'single')  _renderSingle(containerId);
        if (mode === 'multi')   _renderMulti(containerId);
    }

    // ??????????????????????????????????????????
    // setFile � load existing data
    // ??????????????????????????????????????????
    function setFile(containerId, data) {
        var s = _states[containerId];
        if (!s) return;

        if (s.mode === 'avatar') {
            // data = avatarFileId (Guid string) or null
            _avatarSetImage(containerId, data);
        } else if (s.mode === 'single') {
            // data = FileAttachmentResponse object or null
            if (!data) { _singleShowDropZone(containerId); return; }
            s.fileInfo = {
                tempFileId:  null,
                existingId:  data.id,
                docType:     data.docType || s.docType,
                name:        data.originalFileName,
                size:        data.fileSizeBytes,
                ext:         data.fileExtension
            };
            _singleRenderFileCard(containerId);
        } else if (s.mode === 'multi') {
            // data = FileAttachmentResponse[] or []
            s.files = [];
            if (data && data.length) {
                data.forEach(function (f) {
                    s.files.push({
                        id:          _uid(),
                        tempFileId:  null,
                        existingId:  f.id,
                        docType:     f.docType || '',
                        name:        f.originalFileName,
                        size:        f.fileSizeBytes,
                        ext:         f.fileExtension,
                        uploading:   false,
                        progress:    100
                    });
                });
            }
            _multiRenderTable(containerId);
        }
    }

    // ??????????????????????????????????????????
    // getResult � get upload results for form submission
    // ??????????????????????????????????????????
    function getResult(containerId) {
        var s = _states[containerId];
        if (!s) return null;

        if (s.mode === 'avatar') {
            return s.tempFileId || null;
        } else if (s.mode === 'single') {
            if (!s.fileInfo) return null;
            if (s.fileInfo.tempFileId) {
                return { tempFileId: s.fileInfo.tempFileId, docType: s.fileInfo.docType || s.docType };
            }
            // existing file, no new upload
            return null;
        } else if (s.mode === 'multi') {
            var results = [];
            s.files.forEach(function (f) {
                if (f.tempFileId && !f.uploading) {
                    results.push({ tempFileId: f.tempFileId, docType: f.docType });
                }
            });
            return results;
        }
        return null;
    }

    // ??????????????????????????????????????????
    // getFiles � current file list (multi mode)
    // ??????????????????????????????????????????
    function getFiles(containerId) {
        var s = _states[containerId];
        if (!s || s.mode !== 'multi') return [];
        return s.files.map(function (f) {
            return {
                tempFileId: f.tempFileId,
                existingId: f.existingId,
                docType:    f.docType,
                name:       f.name,
                uploading:  !!f.uploading
            };
        });
    }

    // ??????????????????????????????????????????
    // reset
    // ??????????????????????????????????????????
    function reset(containerId) {
        var s = _states[containerId];
        if (!s) return;

        // Abort any in-flight XHRs
        if (s._xhrs) {
            Object.keys(s._xhrs).forEach(function (k) {
                if (s._xhrs[k] && s._xhrs[k].abort) s._xhrs[k].abort();
            });
            s._xhrs = {};
        }

        s.uploading  = false;
        s.tempFileId = null;
        s.fileInfo   = null;
        s.files      = [];

        if (s.mode === 'avatar')  _renderAvatar(containerId);
        if (s.mode === 'single')  _renderSingle(containerId);
        if (s.mode === 'multi')   _renderMulti(containerId);
    }

    // ??????????????????????????????????????????
    // _pick � trigger hidden file input
    // ??????????????????????????????????????????
    function _pick(containerId) {
        var inp = document.getElementById(containerId + '-input');
        if (inp) inp.click();
    }

    function _uid() { return 'fu_' + Math.random().toString(36).substring(2, 9); }

    // ??????????????????????????????????????????
    // VALIDATE
    // ??????????????????????????????????????????
    function _validate(s, file) {
        var ext = _getExt(file.name);
        if (s.accept) {
            var allowed = s.accept.split(',').map(function (e) { return e.trim().toLowerCase(); });
            if (allowed.indexOf(ext) === -1) {
                CF.toast.warning('File type ' + ext + ' is not allowed.');
                return false;
            }
        }
        var maxBytes = s.maxSizeMb * 1024 * 1024;
        if (file.size > maxBytes) {
            CF.toast.warning('File size must not exceed ' + s.maxSizeMb + ' MB.');
            return false;
        }
        return true;
    }

    // ??????????????????????????????????????????
    // UPLOAD with XHR (for progress)
    // ??????????????????????????????????????????
    function _uploadFile(containerId, file, trackId, onProgress, onDone, onFail) {
        var s = _states[containerId];
        if (!s) return;

        var formData = new FormData();
        formData.append('file', file);

        var token = CF.auth.getAccessToken();
        var url = _apiBase() + '/api/v1/files/upload?module=' + encodeURIComponent(s.module);

        var xhr = new XMLHttpRequest();
        s._xhrs[trackId] = xhr;

        xhr.upload.addEventListener('progress', function (e) {
            if (e.lengthComputable) {
                var pct = Math.round((e.loaded / e.total) * 100);
                if (onProgress) onProgress(pct);
            }
        });

        xhr.addEventListener('load', function () {
            delete s._xhrs[trackId];
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    var res = JSON.parse(xhr.responseText);
                    if (res && res.success && res.data) {
                        if (onDone) onDone(res.data);
                        return;
                    }
                    if (onFail) onFail((res && res.message) || 'Upload failed.');
                } catch (e2) {
                    if (onFail) onFail('Upload failed.');
                }
            } else {
                if (onFail) onFail('Upload failed (HTTP ' + xhr.status + ').');
            }
        });

        xhr.addEventListener('error', function () {
            delete s._xhrs[trackId];
            if (onFail) onFail('Upload failed.');
        });

        xhr.addEventListener('abort', function () {
            delete s._xhrs[trackId];
        });

        xhr.open('POST', url, true);
        if (token) xhr.setRequestHeader('Authorization', 'Bearer ' + token);
        xhr.send(formData);
    }

    // ??????????????????????????????????????????????????????????
    //   AVATAR MODE
    // ??????????????????????????????????????????????????????????

    function _renderAvatar(containerId) {
        var s = _states[containerId];
        var el = document.getElementById(containerId);
        if (!el) return;

        var html = '<div class="cf-fu-avatar-wrap">';
        html += '<div class="cf-fu-avatar-circle">';
        html += '<div class="cf-fu-avatar-preview" id="' + containerId + '-preview">';
        html += '<div class="cf-fu-avatar-placeholder"><i class="fa-solid fa-user"></i></div>';
        html += '</div>';

        if (!s.readonly) {
            html += '<button type="button" class="cf-fu-avatar-camera" onclick="CF.fileUpload._pick(\'' + containerId + '\')" title="Upload avatar">';
            html += '<i class="fa-solid fa-camera"></i>';
            html += '</button>';
        }

        html += '</div>';

        if (!s.readonly) {
            html += '<div class="cf-fu-avatar-hint">' + _esc(s.accept.replace(/\./g, '').toUpperCase().replace(/,/g, ', ')) + ' &bull; Max ' + s.maxSizeMb + ' MB</div>';
            html += '<input type="file" id="' + containerId + '-input" class="cf-fu-hidden-input" accept="' + s.accept + '" />';
        }

        html += '</div>';
        el.innerHTML = html;

        if (!s.readonly) {
            var inp = document.getElementById(containerId + '-input');
            if (inp) {
                inp.addEventListener('change', function () {
                    if (inp.files && inp.files[0]) {
                        _avatarHandleFile(containerId, inp.files[0]);
                        inp.value = '';
                    }
                });
            }
        }
    }

    function _avatarHandleFile(containerId, file) {
        var s = _states[containerId];
        if (!s || s.uploading) return;
        if (!_validate(s, file)) return;

        s.uploading  = true;
        s.tempFileId = null;

        // Show local preview + spinner overlay
        var reader = new FileReader();
        reader.onload = function (e) {
            _avatarSetImg(containerId, e.target.result);
            _avatarSetOverlay(containerId, true);
        };
        reader.readAsDataURL(file);

        _uploadFile(containerId, file, 'avatar',
            null, // no progress for avatar
            function (data) {
                s.uploading = false;
                s.tempFileId = data.tempFileId;
                _avatarSetOverlay(containerId, false);
                // re-read for preview
                var r2 = new FileReader();
                r2.onload = function (e2) { _avatarSetImg(containerId, e2.target.result); };
                r2.readAsDataURL(file);
                CF.toast.success('Avatar uploaded.');
                if (s.onUploaded) s.onUploaded(data.tempFileId);
            },
            function (msg) {
                s.uploading  = false;
                s.tempFileId = null;
                _avatarSetOverlay(containerId, false);
                _avatarSetImg(containerId, null);
                CF.toast.error(msg);
            }
        );
    }

    function _avatarSetImg(containerId, src) {
        var preview = document.getElementById(containerId + '-preview');
        if (!preview) return;
        if (src) {
            preview.innerHTML = '<img src="' + src + '" alt="Avatar" class="cf-fu-avatar-img" />';
        } else {
            preview.innerHTML = '<div class="cf-fu-avatar-placeholder"><i class="fa-solid fa-user"></i></div>';
        }
    }

    function _avatarSetOverlay(containerId, show) {
        var circle = document.querySelector('#' + containerId + ' .cf-fu-avatar-circle');
        if (!circle) return;
        var existing = circle.querySelector('.cf-fu-avatar-overlay');
        if (show && !existing) {
            var div = document.createElement('div');
            div.className = 'cf-fu-avatar-overlay';
            div.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i>';
            circle.appendChild(div);
        } else if (!show && existing) {
            existing.remove();
        }
    }

    function _avatarSetImage(containerId, avatarFileId) {
        if (!avatarFileId) { _avatarSetImg(containerId, null); return; }

        _avatarSetOverlay(containerId, true);

        var url   = _apiBase() + '/api/v1/files/' + avatarFileId + '/thumbnail';
        var token = CF.auth.getAccessToken();
        var headers = token ? { 'Authorization': 'Bearer ' + token } : {};

        fetch(url, { headers: headers })
            .then(function (r) { return r.ok ? r.blob() : null; })
            .then(function (blob) {
                _avatarSetOverlay(containerId, false);
                if (!blob) { _avatarSetImg(containerId, null); return; }
                var fr = new FileReader();
                fr.onloadend = function () { _avatarSetImg(containerId, fr.result); };
                fr.readAsDataURL(blob);
            })
            .catch(function () {
                _avatarSetOverlay(containerId, false);
                _avatarSetImg(containerId, null);
            });
    }

    // ??????????????????????????????????????????????????????????
    //   SINGLE MODE
    // ??????????????????????????????????????????????????????????

    function _renderSingle(containerId) {
        var s = _states[containerId];
        var el = document.getElementById(containerId);
        if (!el) return;

        if (s.readonly && !s.fileInfo) {
            el.innerHTML = '<div class="cf-fu-single-empty"><i class="fa-solid fa-file-circle-xmark"></i> No file</div>';
            return;
        }

        if (s.fileInfo) {
            _singleRenderFileCard(containerId);
        } else {
            _singleShowDropZone(containerId);
        }
    }

    function _singleShowDropZone(containerId) {
        var s = _states[containerId];
        var el = document.getElementById(containerId);
        if (!el) return;

        if (s.readonly) {
            el.innerHTML = '<div class="cf-fu-single-empty"><i class="fa-solid fa-file-circle-xmark"></i> No file</div>';
            return;
        }

        var exts = s.accept ? s.accept.replace(/\./g, '').toUpperCase().replace(/,/g, ', ') : 'Any file';

        var html = '<div class="cf-fu-dropzone" id="' + containerId + '-dropzone">';
        html += '<div class="cf-fu-dropzone-inner">';
        html += '<i class="fa-solid fa-cloud-arrow-up cf-fu-dropzone-icon"></i>';
        html += '<div class="cf-fu-dropzone-text">Click or drag file here</div>';
        html += '<div class="cf-fu-dropzone-hint">' + exts + ' &bull; Max ' + s.maxSizeMb + ' MB</div>';
        html += '</div>';
        html += '</div>';
        html += '<input type="file" id="' + containerId + '-input" class="cf-fu-hidden-input" accept="' + (s.accept || '') + '" />';

        el.innerHTML = html;

        var dz  = document.getElementById(containerId + '-dropzone');
        var inp = document.getElementById(containerId + '-input');

        if (dz) {
            dz.addEventListener('click', function () { if (inp) inp.click(); });
            dz.addEventListener('dragover', function (e) { e.preventDefault(); dz.classList.add('cf-fu-dropzone-over'); });
            dz.addEventListener('dragleave', function () { dz.classList.remove('cf-fu-dropzone-over'); });
            dz.addEventListener('drop', function (e) {
                e.preventDefault();
                dz.classList.remove('cf-fu-dropzone-over');
                if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) {
                    _singleHandleFile(containerId, e.dataTransfer.files[0]);
                }
            });
        }

        if (inp) {
            inp.addEventListener('change', function () {
                if (inp.files && inp.files[0]) {
                    _singleHandleFile(containerId, inp.files[0]);
                    inp.value = '';
                }
            });
        }
    }

    function _singleHandleFile(containerId, file) {
        var s = _states[containerId];
        if (!s || s.uploading) return;
        if (!_validate(s, file)) return;

        s.uploading = true;
        s.fileInfo = {
            tempFileId: null,
            existingId: null,
            docType:    s.docType,
            name:       file.name,
            size:       file.size,
            ext:        _getExt(file.name),
            uploading:  true,
            progress:   0
        };

        _singleRenderFileCard(containerId);

        _uploadFile(containerId, file, 'single',
            function (pct) {
                s.fileInfo.progress = pct;
                _singleUpdateProgress(containerId, pct);
            },
            function (data) {
                s.uploading = false;
                s.fileInfo.uploading = false;
                s.fileInfo.tempFileId = data.tempFileId;
                s.fileInfo.progress = 100;
                _singleRenderFileCard(containerId);
                CF.toast.success('File uploaded.');
                if (s.onUploaded) s.onUploaded(data);
            },
            function (msg) {
                s.uploading = false;
                s.fileInfo = null;
                _singleShowDropZone(containerId);
                CF.toast.error(msg);
            }
        );
    }

    function _singleRenderFileCard(containerId) {
        var s = _states[containerId];
        var el = document.getElementById(containerId);
        if (!el || !s.fileInfo) return;

        var f = s.fileInfo;
        var icon = _fileIcon(f.ext);
        var isUploading = f.uploading;
        var pct = f.progress || 0;

        var html = '<div class="cf-fu-file-card">';
        html += '<div class="cf-fu-file-card-icon"><i class="' + icon + '"></i></div>';
        html += '<div class="cf-fu-file-card-info">';
        html += '<div class="cf-fu-file-card-name">' + _esc(f.name) + '</div>';
        html += '<div class="cf-fu-file-card-meta">';
        html += _esc(f.ext.replace('.', '').toUpperCase());
        if (!isUploading) html += ' &bull; ' + _formatSize(f.size);
        if (f.docType) html += ' &bull; ' + _esc(f.docType);
        html += '</div>';

        if (isUploading) {
            html += '<div class="cf-fu-progress-wrap">';
            html += '<div class="cf-fu-progress-bar" id="' + containerId + '-progress" style="width:' + pct + '%"></div>';
            html += '</div>';
        } else {
            html += '<div class="cf-fu-file-card-status"><i class="fa-solid fa-circle-check"></i> Uploaded</div>';
        }

        html += '</div>';

        // Actions
        html += '<div class="cf-fu-file-card-actions">';
        if (!s.readonly && !isUploading) {
            // Preview/download for existing files
            if (f.existingId) {
                if (_isImage(f.ext)) {
                    html += '<button type="button" class="cf-fu-file-action" onclick="CF.fileUpload._previewFile(\'' + containerId + '\',\'' + f.existingId + '\')" title="Preview"><i class="fa-solid fa-eye"></i></button>';
                }
                html += '<button type="button" class="cf-fu-file-action" onclick="CF.fileUpload._downloadFile(\'' + f.existingId + '\')" title="Download"><i class="fa-solid fa-download"></i></button>';
            }
            html += '<button type="button" class="cf-fu-file-action cf-fu-file-action-danger" onclick="CF.fileUpload._singleRemove(\'' + containerId + '\')" title="Remove"><i class="fa-solid fa-trash"></i></button>';
        } else if (s.readonly && f.existingId) {
            if (_isImage(f.ext)) {
                html += '<button type="button" class="cf-fu-file-action" onclick="CF.fileUpload._previewFile(\'' + containerId + '\',\'' + f.existingId + '\')" title="Preview"><i class="fa-solid fa-eye"></i></button>';
            }
            html += '<button type="button" class="cf-fu-file-action" onclick="CF.fileUpload._downloadFile(\'' + f.existingId + '\')" title="Download"><i class="fa-solid fa-download"></i></button>';
        }
        html += '</div>';

        html += '</div>';

        el.innerHTML = html;
    }

    function _singleUpdateProgress(containerId, pct) {
        var bar = document.getElementById(containerId + '-progress');
        if (bar) bar.style.width = pct + '%';
    }

    function _singleRemove(containerId) {
        var s = _states[containerId];
        if (!s) return;

        // Abort if uploading
        if (s._xhrs['single']) {
            s._xhrs['single'].abort();
            delete s._xhrs['single'];
        }

        s.uploading = false;
        s.fileInfo  = null;
        _singleShowDropZone(containerId);
        if (s.onRemoved) s.onRemoved();
    }

    // ??????????????????????????????????????????????????????????
    //   MULTI MODE
    // ??????????????????????????????????????????????????????????

    function _renderMulti(containerId) {
        var s = _states[containerId];
        var el = document.getElementById(containerId);
        if (!el) return;

        var html = '';

        // Drop zone (if not readonly and below max)
        if (!s.readonly) {
            var exts = s.accept ? s.accept.replace(/\./g, '').toUpperCase().replace(/,/g, ', ') : 'Any file';
            html += '<div class="cf-fu-dropzone cf-fu-dropzone-compact" id="' + containerId + '-dropzone">';
            html += '<div class="cf-fu-dropzone-inner">';
            html += '<i class="fa-solid fa-cloud-arrow-up cf-fu-dropzone-icon"></i>';
            html += '<div class="cf-fu-dropzone-text">Click or drag files here</div>';
            html += '<div class="cf-fu-dropzone-hint">' + exts + ' &bull; Max ' + s.maxSizeMb + ' MB &bull; Max ' + s.maxFiles + ' files</div>';
            html += '</div>';
            html += '</div>';
            html += '<input type="file" id="' + containerId + '-input" class="cf-fu-hidden-input" accept="' + (s.accept || '') + '" multiple />';
        }

        // Table
        html += '<div id="' + containerId + '-table-wrap">';
        html += _multiTableHtml(containerId);
        html += '</div>';

        el.innerHTML = html;

        if (!s.readonly) {
            var dz  = document.getElementById(containerId + '-dropzone');
            var inp = document.getElementById(containerId + '-input');

            if (dz) {
                dz.addEventListener('click', function () { if (inp) inp.click(); });
                dz.addEventListener('dragover', function (e) { e.preventDefault(); dz.classList.add('cf-fu-dropzone-over'); });
                dz.addEventListener('dragleave', function () { dz.classList.remove('cf-fu-dropzone-over'); });
                dz.addEventListener('drop', function (e) {
                    e.preventDefault();
                    dz.classList.remove('cf-fu-dropzone-over');
                    if (e.dataTransfer && e.dataTransfer.files) {
                        _multiHandleFiles(containerId, e.dataTransfer.files);
                    }
                });
            }

            if (inp) {
                inp.addEventListener('change', function () {
                    if (inp.files && inp.files.length) {
                        _multiHandleFiles(containerId, inp.files);
                        inp.value = '';
                    }
                });
            }
        }
    }

    function _multiTableHtml(containerId) {
        var s = _states[containerId];
        if (!s || !s.files.length) {
            if (s.readonly) {
                return '<div class="cf-fu-multi-empty"><i class="fa-solid fa-file-circle-xmark"></i> No files</div>';
            }
            return '';
        }

        var hasDocTypes = s.docTypes && s.docTypes.length > 0;

        var html = '<table class="cf-fu-table">';
        html += '<thead><tr>';
        html += '<th class="cf-fu-table-num">#</th>';
        html += '<th>File Name</th>';
        html += '<th class="cf-fu-table-size">Size</th>';
        if (hasDocTypes) html += '<th class="cf-fu-table-doctype">Doc Type</th>';
        html += '<th class="cf-fu-table-actions"></th>';
        html += '</tr></thead>';
        html += '<tbody>';

        s.files.forEach(function (f, idx) {
            var icon = _fileIcon(f.ext);
            html += '<tr id="' + containerId + '-row-' + f.id + '">';
            html += '<td class="cf-fu-table-num">' + (idx + 1) + '</td>';
            html += '<td>';
            html += '<div class="cf-fu-table-file">';
            html += '<i class="' + icon + ' cf-fu-table-file-icon"></i>';
            html += '<span class="cf-fu-table-file-name">' + _esc(f.name) + '</span>';
            html += '</div>';
            if (f.uploading) {
                html += '<div class="cf-fu-progress-wrap cf-fu-progress-wrap-sm">';
                html += '<div class="cf-fu-progress-bar" id="' + containerId + '-progress-' + f.id + '" style="width:' + (f.progress || 0) + '%"></div>';
                html += '</div>';
            }
            html += '</td>';
            html += '<td class="cf-fu-table-size">';
            if (f.uploading) {
                html += '<span class="cf-fu-table-uploading">' + (f.progress || 0) + '%</span>';
            } else {
                html += _formatSize(f.size);
            }
            html += '</td>';

            if (hasDocTypes) {
                html += '<td class="cf-fu-table-doctype">';
                if (s.readonly) {
                    html += _esc(f.docType || '�');
                } else {
                    html += '<select class="cf-fu-doctype-select" onchange="CF.fileUpload._multiSetDocType(\'' + containerId + '\',\'' + f.id + '\',this.value)">';
                    html += '<option value="">Select</option>';
                    s.docTypes.forEach(function (dt) {
                        html += '<option value="' + _esc(dt) + '"' + (f.docType === dt ? ' selected' : '') + '>' + _esc(dt) + '</option>';
                    });
                    html += '</select>';
                }
                html += '</td>';
            }

            html += '<td class="cf-fu-table-actions">';
            if (f.uploading) {
                html += '<button type="button" class="cf-fu-file-action cf-fu-file-action-danger" onclick="CF.fileUpload._multiRemove(\'' + containerId + '\',\'' + f.id + '\')" title="Cancel"><i class="fa-solid fa-xmark"></i></button>';
            } else {
                if (f.existingId) {
                    if (_isImage(f.ext)) {
                        html += '<button type="button" class="cf-fu-file-action" onclick="CF.fileUpload._previewFile(\'' + containerId + '\',\'' + f.existingId + '\')" title="Preview"><i class="fa-solid fa-eye"></i></button>';
                    }
                    html += '<button type="button" class="cf-fu-file-action" onclick="CF.fileUpload._downloadFile(\'' + f.existingId + '\')" title="Download"><i class="fa-solid fa-download"></i></button>';
                }
                if (!s.readonly) {
                    html += '<button type="button" class="cf-fu-file-action cf-fu-file-action-danger" onclick="CF.fileUpload._multiRemove(\'' + containerId + '\',\'' + f.id + '\')" title="Remove"><i class="fa-solid fa-trash"></i></button>';
                }
            }
            html += '</td>';

            html += '</tr>';
        });

        html += '</tbody></table>';
        html += '<div class="cf-fu-multi-footer">' + s.files.length + ' of ' + s.maxFiles + ' files</div>';

        return html;
    }

    function _multiRenderTable(containerId) {
        var wrap = document.getElementById(containerId + '-table-wrap');
        if (wrap) wrap.innerHTML = _multiTableHtml(containerId);
    }

    function _multiHandleFiles(containerId, fileList) {
        var s = _states[containerId];
        if (!s) return;

        var remaining = s.maxFiles - s.files.length;
        if (remaining <= 0) {
            CF.toast.warning('Maximum ' + s.maxFiles + ' files allowed.');
            return;
        }

        var filesToAdd = [];
        for (var i = 0; i < fileList.length && i < remaining; i++) {
            if (_validate(s, fileList[i])) {
                filesToAdd.push(fileList[i]);
            }
        }

        if (fileList.length > remaining) {
            CF.toast.warning('Only ' + remaining + ' more file(s) can be added.');
        }

        filesToAdd.forEach(function (file) {
            var fid = _uid();
            var entry = {
                id:         fid,
                tempFileId: null,
                existingId: null,
                docType:    '',
                name:       file.name,
                size:       file.size,
                ext:        _getExt(file.name),
                uploading:  true,
                progress:   0
            };
            s.files.push(entry);

            _multiRenderTable(containerId);

            _uploadFile(containerId, file, fid,
                function (pct) {
                    entry.progress = pct;
                    var bar = document.getElementById(containerId + '-progress-' + fid);
                    if (bar) bar.style.width = pct + '%';
                    // Update size cell with percentage
                    var row = document.getElementById(containerId + '-row-' + fid);
                    if (row) {
                        var sizeCell = row.querySelector('.cf-fu-table-uploading');
                        if (sizeCell) sizeCell.textContent = pct + '%';
                    }
                },
                function (data) {
                    entry.uploading   = false;
                    entry.tempFileId  = data.tempFileId;
                    entry.progress    = 100;
                    _multiRenderTable(containerId);
                    if (s.onUploaded) s.onUploaded(data);
                },
                function (msg) {
                    // Remove failed entry
                    s.files = s.files.filter(function (x) { return x.id !== fid; });
                    _multiRenderTable(containerId);
                    CF.toast.error(msg);
                }
            );
        });
    }

    function _multiRemove(containerId, fileId) {
        var s = _states[containerId];
        if (!s) return;

        // Abort XHR if uploading
        if (s._xhrs[fileId]) {
            s._xhrs[fileId].abort();
            delete s._xhrs[fileId];
        }

        s.files = s.files.filter(function (f) { return f.id !== fileId; });
        _multiRenderTable(containerId);
        if (s.onRemoved) s.onRemoved(fileId);
    }

    function _multiSetDocType(containerId, fileId, value) {
        var s = _states[containerId];
        if (!s) return;
        var f = s.files.find(function (x) { return x.id === fileId; });
        if (f) f.docType = value;
    }

    // ??????????????????????????????????????????
    // PREVIEW / DOWNLOAD HELPERS
    // ??????????????????????????????????????????
    function _previewFile(containerId, fileId) {
        var url   = _apiBase() + '/api/v1/files/' + fileId + '/thumbnail';
        var token = CF.auth.getAccessToken();
        var headers = token ? { 'Authorization': 'Bearer ' + token } : {};

        fetch(url, { headers: headers })
            .then(function (r) { return r.ok ? r.blob() : null; })
            .then(function (blob) {
                if (!blob) { CF.toast.error('Preview not available.'); return; }
                var objUrl = URL.createObjectURL(blob);
                window.open(objUrl, '_blank');
            })
            .catch(function () { CF.toast.error('Preview failed.'); });
    }

    function _downloadFile(fileId) {
        var url   = _apiBase() + '/api/v1/files/' + fileId + '/download';
        var token = CF.auth.getAccessToken();

        // Use fetch + blob to handle auth header
        fetch(url, { headers: token ? { 'Authorization': 'Bearer ' + token } : {} })
            .then(function (r) {
                if (!r.ok) throw new Error('Download failed');
                var disposition = r.headers.get('Content-Disposition') || '';
                var match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                var filename = match ? match[1].replace(/['"]/g, '') : 'download';
                return r.blob().then(function (blob) { return { blob: blob, filename: filename }; });
            })
            .then(function (result) {
                var a = document.createElement('a');
                a.href = URL.createObjectURL(result.blob);
                a.download = result.filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(a.href);
            })
            .catch(function () { CF.toast.error('Download failed.'); });
    }

    // ??????????????????????????????????????????????????????????
    //   PUBLIC API
    // ??????????????????????????????????????????????????????????
    return {
        init:       init,
        setFile:    setFile,
        getResult:  getResult,
        getFiles:   getFiles,
        reset:      reset,
        _pick:      _pick,
        // Internal actions exposed for onclick handlers
        _singleRemove:    _singleRemove,
        _multiRemove:     _multiRemove,
        _multiSetDocType: _multiSetDocType,
        _previewFile:     _previewFile,
        _downloadFile:    _downloadFile
    };

})();

// ??????????????????????????????????????????????????????????
// Backward compatibility � CF.avatarUpload ? CF.fileUpload
// ??????????????????????????????????????????????????????????
CF.avatarUpload = {
    init: function (containerId, options) {
        var opts = options || {};
        opts.mode = 'avatar';
        CF.fileUpload.init(containerId, opts);
    },
    setImage: function (containerId, avatarFileId) {
        CF.fileUpload.setFile(containerId, avatarFileId);
    },
    getTempFileId: function (containerId) {
        return CF.fileUpload.getResult(containerId);
    },
    reset: function (containerId) {
        CF.fileUpload.reset(containerId);
    },
    _pick: function (containerId) {
        CF.fileUpload._pick(containerId);
    }
};
